import React, { forwardRef, useRef } from "react";

import { Formik, FormikConfig, FormikProps, FormikValues } from "formik";

import FormWrapper from "./FormWrapper";
import { getFieldsWithServerError } from "./ScrollToErrorField/utils";

export interface FormProps<T extends FormikValues = FormikValues> {
  /** Formik configuration: initialValues, validationSchema, onSubmit, etc. */
  formikProps: FormikConfig<T>;
  /** Props forwarded to the HTML form element. */
  formProps?: React.ComponentProps<"form">;
  /** Auto-scroll to the first error field on submit. */
  scrollToErrorField?: boolean;
  /** Additional class name for the form wrapper. */
  className?: string;
  /** Render children directly or as a function receiving FormikProps. */
  children: React.ReactNode | ((props: FormikProps<T>) => React.ReactNode);
}

const Form = forwardRef<HTMLFormElement, FormProps>(
  (
    { className, children, formikProps, formProps, scrollToErrorField = false },
    ref
  ) => {
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const handleSubmit: FormikConfig<FormikValues>["onSubmit"] = (
      values,
      actions
    ) => {
      const fieldsWithServerError = getFieldsWithServerError(
        formikRef.current?.status
      );

      if (fieldsWithServerError.length > 0) {
        actions.setSubmitting(false);

        return undefined;
      }

      return formikProps.onSubmit(values, actions);
    };

    return (
      <Formik
        innerRef={formikRef as React.Ref<FormikProps<FormikValues>>}
        {...formikProps}
        onSubmit={handleSubmit}
      >
        {props => (
          <FormWrapper {...{ className, formProps, ref, scrollToErrorField }}>
            {typeof children === "function" ? children(props) : children}
          </FormWrapper>
        )}
      </Formik>
    );
  }
);

Form.displayName = "Form";

export { Form };
